import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent
} from '@mui/material';
import { Delete, Edit, Add, Phone, Settings, History } from '@mui/icons-material';

const GestionContactosAlarma = () => {
  const [contactos, setContactos] = useState([]);
  const [configuracion, setConfiguracion] = useState({ frecuenciaMinutos: 10, activo: true });
  const [historial, setHistorial] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openHistorialDialog, setOpenHistorialDialog] = useState(false);
  const [editando, setEditando] = useState(null);
  const [formData, setFormData] = useState({ telefono: '', nombre: '', activo: true, telegramChatId: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    cargarContactos();
    cargarConfiguracion();
  }, []);

  const cargarContactos = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/contactos-alarma', {
        headers: { 'x-access-token': token }
      });
      if (response.ok) {
        const data = await response.json();
        setContactos(data);
      }
    } catch (error) {
      console.error('Error al cargar contactos:', error);
      setError('Error al cargar contactos');
    }
  };

  const cargarConfiguracion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/configuracion-alarma', {
        headers: { 'x-access-token': token }
      });
      if (response.ok) {
        const data = await response.json();
        setConfiguracion(data);
      }
    } catch (error) {
      console.error('Error al cargar configuración:', error);
    }
  };

  const cargarHistorial = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/historial-alarmas?limit=20', {
        headers: { 'x-access-token': token }
      });
      if (response.ok) {
        const data = await response.json();
        setHistorial(data);
        setOpenHistorialDialog(true);
      }
    } catch (error) {
      console.error('Error al cargar historial:', error);
      setError('Error al cargar historial');
    }
  };

  const handleOpenDialog = (contacto = null) => {
    if (contacto) {
      setEditando(contacto._id);
      setFormData({
        telefono: contacto.telefono || '',
        nombre: contacto.nombre,
        activo: contacto.activo,
        telegramChatId: contacto.telegramChatId || ''
      });
    } else {
      setEditando(null);
      setFormData({ telefono: '', nombre: '', activo: true, telegramChatId: '' });
    }
    setOpenDialog(true);
    setError('');
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditando(null);
    setFormData({ telefono: '', nombre: '', activo: true, telegramChatId: '' });
    setError('');
  };

  const handleGuardarContacto = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Validar formato de teléfono solo si se proporciona
      if (formData.telefono && !/^\+\d{10,15}$/.test(formData.telefono)) {
        setError('El teléfono debe estar en formato internacional (ej: +573001234567)');
        return;
      }
      
      // Validar que al menos tenga teléfono o Telegram
      if (!formData.telefono && !formData.telegramChatId) {
        setError('Debe proporcionar al menos un teléfono o un Chat ID de Telegram');
        return;
      }

      const url = editando ? `/api/contactos-alarma/${editando}` : '/api/contactos-alarma';
      const method = editando ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess(editando ? 'Contacto actualizado correctamente' : 'Contacto agregado correctamente');
        handleCloseDialog();
        cargarContactos();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const data = await response.json();
        setError(data.message || 'Error al guardar contacto');
      }
    } catch (error) {
      console.error('Error al guardar contacto:', error);
      setError('Error al guardar contacto');
    }
  };

  const handleEliminarContacto = async (id) => {
    if (!window.confirm('¿Está seguro de eliminar este contacto?')) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`/api/contactos-alarma/${id}`, {
        method: 'DELETE',
        headers: { 'x-access-token': token }
      });

      if (response.ok) {
        setSuccess('Contacto eliminado correctamente');
        cargarContactos();
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error al eliminar contacto:', error);
      setError('Error al eliminar contacto');
    }
  };

  const handleGuardarConfiguracion = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/configuracion-alarma', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-access-token': token
        },
        body: JSON.stringify(configuracion)
      });

      if (response.ok) {
        setSuccess('Configuración actualizada correctamente');
        setTimeout(() => setSuccess(''), 3000);
      }
    } catch (error) {
      console.error('Error al guardar configuración:', error);
      setError('Error al guardar configuración');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" gutterBottom sx={{ color: '#B8F1FF', mb: 3 }}>
        <Phone sx={{ verticalAlign: 'middle', mr: 1 }} />
        Gestión de Contactos para Alarmas
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Configuración Global */}
      <Card sx={{ mb: 3, backgroundColor: '#1a1a1a' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ color: '#B8F1FF', display: 'flex', alignItems: 'center' }}>
            <Settings sx={{ mr: 1 }} />
            Configuración de Alarmas
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <TextField
                label="Frecuencia de alarmas (minutos)"
                type="number"
                fullWidth
                value={configuracion.frecuenciaMinutos}
                onChange={(e) => setConfiguracion({ ...configuracion, frecuenciaMinutos: parseInt(e.target.value) })}
                inputProps={{ min: 1, max: 1440 }}
                helperText="Tiempo mínimo entre alarmas para la misma máquina"
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <FormControlLabel
                control={
                  <Switch
                    checked={configuracion.activo}
                    onChange={(e) => setConfiguracion({ ...configuracion, activo: e.target.checked })}
                    color="primary"
                  />
                }
                label={configuracion.activo ? "Alarmas activas" : "Alarmas desactivadas"}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={handleGuardarConfiguracion}
              >
                Guardar Configuración
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Botones de acción */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Agregar Contacto
        </Button>
        <Button
          variant="outlined"
          color="info"
          startIcon={<History />}
          onClick={cargarHistorial}
        >
          Ver Historial
        </Button>
      </Box>

      {/* Tabla de contactos */}
      <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: '#B8F1FF' }}>Nombre</TableCell>
              <TableCell sx={{ color: '#B8F1FF' }}>Teléfono</TableCell>
              <TableCell sx={{ color: '#B8F1FF' }}>Telegram</TableCell>
              <TableCell sx={{ color: '#B8F1FF' }}>Estado</TableCell>
              <TableCell sx={{ color: '#B8F1FF' }}>Fecha de Registro</TableCell>
              <TableCell sx={{ color: '#B8F1FF' }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contactos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: '#999' }}>
                  No hay contactos registrados
                </TableCell>
              </TableRow>
            ) : (
              contactos.map((contacto) => (
                <TableRow key={contacto._id}>
                  <TableCell sx={{ color: '#fff' }}>{contacto.nombre}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>{contacto.telefono || '-'}</TableCell>
                  <TableCell sx={{ color: '#fff' }}>
                    {contacto.telegramChatId ? (
                      <Chip label="✓ Telegram" color="info" size="small" />
                    ) : (
                      <span style={{ color: '#666' }}>-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={contacto.activo ? 'Activo' : 'Inactivo'}
                      color={contacto.activo ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{ color: '#999' }}>
                    {new Date(contacto.createdAt).toLocaleDateString('es-ES')}
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleOpenDialog(contacto)}
                      size="small"
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleEliminarContacto(contacto._id)}
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog para agregar/editar contacto */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editando ? 'Editar Contacto' : 'Agregar Contacto'}
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          <TextField
            label="Nombre"
            fullWidth
            margin="normal"
            value={formData.nombre}
            onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
          />
          <TextField
            label="Teléfono (formato internacional) - Opcional"
            fullWidth
            margin="normal"
            value={formData.telefono}
            onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
            placeholder="+573001234567"
            helperText="Opcional: Debe incluir el código de país con + (ej: +57 para Colombia)"
          />
          <TextField
            label="Chat ID de Telegram - Opcional"
            fullWidth
            margin="normal"
            value={formData.telegramChatId}
            onChange={(e) => setFormData({ ...formData, telegramChatId: e.target.value })}
            placeholder="123456789"
            helperText="Opcional: Obtén tu Chat ID con: node obtener-chat-ids-telegram.js"
          />
          <Alert severity="info" sx={{ mt: 2, mb: 1 }}>
            Debe proporcionar al menos un Teléfono o un Chat ID de Telegram para alarmas
          </Alert>
          <FormControlLabel
            control={
              <Switch
                checked={formData.activo}
                onChange={(e) => setFormData({ ...formData, activo: e.target.checked })}
              />
            }
            label="Activo"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancelar</Button>
          <Button onClick={handleGuardarContacto} variant="contained" color="primary">
            {editando ? 'Actualizar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de historial */}
      <Dialog open={openHistorialDialog} onClose={() => setOpenHistorialDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Historial de Alarmas Enviadas</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Máquina</TableCell>
                  <TableCell>Amperaje</TableCell>
                  <TableCell>Máximo</TableCell>
                  <TableCell>Contactos</TableCell>
                  <TableCell>Fecha/Hora</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historial.map((alarma) => (
                  <TableRow key={alarma._id}>
                    <TableCell>{alarma.maquinaId}</TableCell>
                    <TableCell>{alarma.amperaje.toFixed(1)}A</TableCell>
                    <TableCell>{alarma.amperajeMaximo}A</TableCell>
                    <TableCell>{alarma.telefonosEnviados.length}</TableCell>
                    <TableCell>
                      {new Date(alarma.fechaEnvio).toLocaleString('es-ES')}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenHistorialDialog(false)}>Cerrar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default GestionContactosAlarma;
